/**
 * Custom hook for managing Timeline keyboard navigation and shortcuts.
 *
 * Handles:
 * - Display mode: Arrow key navigation, Enter/Right to select, Left to collapse, Escape to clear
 * - Display mode: Press "e" on focused item to request edit mode
 * - Edit mode: Alt+Arrow navigation, Tab to focus input, Escape to exit input/clear focus
 * - Edit mode item actions: Single key shortcuts (F, L, R, D, E) when item is focused
 * - Edit mode: Press "e" on focused item to focus content editor (TipTap)
 */
import { useCallback, useRef } from "react";
import type { ListImperativeAPI } from "react-window";
import { TimelineItemAction, TimelineItemData } from "../../TimelineItem/types";
import { focusContentEditorInItem } from "../utils/domUtils";

/**
 * Configuration for the keyboard navigation hook
 */
interface UseTimelineKeyboardConfig<T extends string = string> {
  /** Current mode - display or edit */
  mode: "display" | "edit";
  /** Items currently displayed in the timeline */
  displayItems: TimelineItemData<T>[];
  /** Currently focused item ID */
  focusedItemId: string | null;
  /** Index of the focused item in displayItems */
  focusedItemIndex: number;
  /** Callback to set the focused item ID */
  setFocusedItemId: (id: string | null) => void;
  /** Callback when an item is clicked (display mode) */
  handleItemClick: (item: TimelineItemData<T>) => void;
  /** Callback to collapse an item (display mode) */
  collapseItem: (itemId: string) => void;
  /** Callback when an action button is clicked (edit mode) */
  onItemActionClick?: (itemId: string, action: TimelineItemAction) => void;
  /** Callback when add entry is triggered (edit mode) */
  onAddEntry?: () => void;
  /** Callback when user requests edit mode (display mode, pressing "e" on focused item) */
  onRequestEditMode?: (focusedItemId: string) => void;
  /** Ref to the virtualized list for scrolling */
  listRef: React.RefObject<ListImperativeAPI | null>;
  /** Ref to the container element */
  containerRef: React.RefObject<HTMLDivElement | null>;
}

/**
 * Return type for the keyboard navigation hook
 */
interface UseTimelineKeyboardReturn {
  /** Keyboard event handler for the container */
  handleKeyDown: (e: React.KeyboardEvent) => void;
  /** Focus event handler for tracking last active item */
  handleFocusIn: (e: React.FocusEvent) => void;
}

/**
 * Helper function to find which timeline item contains a DOM element
 */
const findItemIdFromElement = (element: Element | null): string | null => {
  if (!element) return null;
  const itemElement = element.closest("[data-timeline-item-id]");
  return itemElement?.getAttribute("data-timeline-item-id") ?? null;
};

/**
 * Helper function to focus the first input in a timeline item
 */
const focusFirstInputInItem = (itemId: string): void => {
  const itemElement = document.querySelector(
    `[data-timeline-item-id="${itemId}"]`,
  );
  if (!itemElement) return;

  // Try to focus the date picker input first
  const dateInput = itemElement.querySelector(
    ".indigo-timeline-item__date-picker input",
  ) as HTMLInputElement | null;
  if (dateInput) {
    dateInput.focus();
    return;
  }

  // Fallback: focus the first focusable input
  const firstInput = itemElement.querySelector(
    'input, textarea, [contenteditable="true"]',
  ) as HTMLElement | null;
  firstInput?.focus();
};

/**
 * Check if any Ant Design popup is currently open
 */
const hasOpenPopup = (): boolean => {
  return !!document.querySelector(
    ".ant-picker-dropdown:not(.ant-picker-dropdown-hidden), .ant-select-dropdown:not(.ant-select-dropdown-hidden)",
  );
};

/**
 * Hook for managing Timeline keyboard navigation and shortcuts
 */
export const useTimelineKeyboard = <T extends string = string>({
  mode,
  displayItems,
  focusedItemId,
  focusedItemIndex,
  setFocusedItemId,
  handleItemClick,
  collapseItem,
  onItemActionClick,
  onAddEntry,
  onRequestEditMode,
  listRef,
  containerRef,
}: UseTimelineKeyboardConfig<T>): UseTimelineKeyboardReturn => {
  // Track the last item that had focus (for escape key handling after popup closes)
  const lastActiveItemIdRef = useRef<string | null>(null);

  // Track focus entering items (for escape key handling + seen tracking)
  const handleFocusIn = useCallback(
    (e: React.FocusEvent) => {
      if (mode !== "edit") return;
      const itemId = findItemIdFromElement(e.target);
      if (itemId) {
        lastActiveItemIdRef.current = itemId;
        // Update focusedItemId so onFocusedItemChange fires (for seen tracking)
        if (itemId !== focusedItemId) {
          setFocusedItemId(itemId);
        }
      }
    },
    [mode, focusedItemId, setFocusedItemId],
  );

  // Keyboard navigation handler for display mode
  const handleDisplayModeKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (displayItems.length === 0) return;

      switch (e.key) {
        case "ArrowDown": {
          e.preventDefault();
          // Move to next item (or first if none focused)
          const nextIndex =
            focusedItemIndex === -1
              ? 0
              : Math.min(focusedItemIndex + 1, displayItems.length - 1);
          setFocusedItemId(displayItems[nextIndex]?.id ?? null);
          listRef.current?.scrollToRow({
            index: nextIndex,
            align: "auto",
            behavior: "instant",
          });
          // Keep DOM focus on the container so subsequent keys keep landing
          // here (mirrors edit mode). Without this, anything that scrolls the
          // focused row out of view and back can drop focus and kill nav.
          containerRef.current?.focus({ preventScroll: true });
          break;
        }
        case "ArrowUp": {
          e.preventDefault();
          // Move to previous item (or last if none focused)
          const prevIndex =
            focusedItemIndex === -1
              ? displayItems.length - 1
              : Math.max(focusedItemIndex - 1, 0);
          setFocusedItemId(displayItems[prevIndex]?.id ?? null);
          listRef.current?.scrollToRow({
            index: prevIndex,
            align: "auto",
            behavior: "instant",
          });
          // Keep DOM focus on the container (see ArrowDown above).
          containerRef.current?.focus({ preventScroll: true });
          break;
        }
        case "ArrowRight":
        case "Enter": {
          e.preventDefault();
          // The PDF previewer registers a document-level Left/Right key handler
          // that flips the page by ±1. While the timeline owns keyboard focus,
          // Right means "open/jump to this entry's start page" — stop the event
          // so the previewer doesn't also advance a page on top of it. Scoped
          // to ArrowRight: the previewer ignores Enter, so don't swallow it.
          if (e.key === "ArrowRight") {
            e.stopPropagation();
          }
          // Select/activate the focused item
          if (focusedItemId) {
            const item = displayItems.find((i) => i.id === focusedItemId);
            if (item) handleItemClick(item);
          }
          break;
        }
        case "ArrowLeft": {
          e.preventDefault();
          // Same as ArrowRight: keep the PDF previewer's document-level handler
          // from also stepping back a page when the timeline owns focus.
          e.stopPropagation();
          // Collapse and deselect the focused item
          if (focusedItemId) {
            collapseItem(focusedItemId);
          }
          break;
        }
        case "Escape": {
          setFocusedItemId(null);
          break;
        }
        case "s":
        case "f": {
          // Only trigger if the key is pressed alone (not part of a modifier key combination)
          if (focusedItemId && !e.ctrlKey && !e.metaKey && !e.altKey) {
            e.preventDefault();
            onItemActionClick?.(focusedItemId, "star");
          }
          break;
        }
        case "e": {
          // Request edit mode when an item is focused (no modifiers)
          if (focusedItemId && !e.ctrlKey && !e.metaKey && !e.altKey) {
            e.preventDefault();
            onRequestEditMode?.(focusedItemId);
          }
          break;
        }
      }
    },
    [
      focusedItemIndex,
      focusedItemId,
      displayItems,
      handleItemClick,
      setFocusedItemId,
      collapseItem,
      listRef,
      containerRef,
      onItemActionClick,
      onRequestEditMode,
    ],
  );

  // Keyboard navigation handler for edit mode
  const handleEditModeKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (displayItems.length === 0) return;

      // Option/Alt + Arrow for item navigation and actions
      if (e.altKey && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
          e.preventDefault();

          // Determine current index: prioritize the item containing the cursor,
          // then fall back to focusedItemId (which may be stale if user scrolled away)
          let currentIndex = -1;

          // First, check if cursor is currently inside a timeline item
          const activeElement = document.activeElement;
          const activeItemId = findItemIdFromElement(activeElement);
          if (activeItemId) {
            currentIndex = displayItems.findIndex(
              (item) => item.id === activeItemId,
            );
          }

          // Fall back to focusedItemId if cursor isn't in any item
          if (currentIndex === -1) {
            currentIndex = focusedItemIndex;
          }

          const nextIndex =
            e.key === "ArrowDown"
              ? currentIndex === -1
                ? 0
                : Math.min(currentIndex + 1, displayItems.length - 1)
              : currentIndex === -1
                ? displayItems.length - 1
                : Math.max(currentIndex - 1, 0);

          const nextItemId = displayItems[nextIndex]?.id;
          if (nextItemId) {
            setFocusedItemId(nextItemId);
            listRef.current?.scrollToRow({
              index: nextIndex,
              align: "start",
              // react-window >=2.3.2 forces instant scrolls on dynamic-height
              // lists so it can correct the offset as rows are measured.
              behavior: "instant",
            });

            // Focus the container so subsequent keyboard events are captured
            containerRef.current?.focus();
          }
          return;
        }

        // Option+Right: Open PDF (parallel to ArrowRight in display mode)
        if (e.key === "ArrowRight" && focusedItemId) {
          e.preventDefault();
          onItemActionClick?.(focusedItemId, "pdf");
          return;
        }
      }

      // Ctrl+Cmd+N (Mac) / Ctrl+Alt+N (Windows): Add new entry
      if (
        e.key.toLowerCase() === "n" &&
        e.ctrlKey &&
        (e.metaKey || e.altKey) &&
        onAddEntry
      ) {
        e.preventDefault();
        onAddEntry();
        return;
      }

      // Check if the container itself has focus (keyboard navigation mode),
      // as opposed to an input field within an item
      const isContainerFocused =
        document.activeElement === containerRef.current;

      // Tab key: focus first input in the focused item (only from keyboard navigation mode)
      // When already inside an input, let the browser's default Tab cycle through fields
      if (
        e.key === "Tab" &&
        !e.shiftKey &&
        focusedItemId &&
        isContainerFocused
      ) {
        e.preventDefault();
        focusFirstInputInItem(focusedItemId);
        setFocusedItemId(null); // Clear focus ring since user is now in input
        return;
      }

      // Escape key: exit input and restore item focus
      // Behavior:
      // 1. If a dropdown/datepicker popup is open → let default behavior close it
      // 2. If an input within an item has focus → unfocus from input, set focusedItemId on current item
      // 3. If the item has focus (focusedItemId is set) → clear focus entirely
      if (e.key === "Escape") {
        if (hasOpenPopup()) {
          // Let the popup component handle closing itself
          return;
        }

        const activeElement = document.activeElement;
        const itemIdFromElement = findItemIdFromElement(activeElement);
        // Use tracked last active item if we can't find it from current focus
        // (happens when popup closes and focus moves to body)
        const itemId = itemIdFromElement || lastActiveItemIdRef.current;

        // Check if we're focused on an input within an item (not the container itself)
        const isWithinItem =
          itemIdFromElement && activeElement !== containerRef.current;
        // Also treat focus on body as "was in an item" if we have a tracked item
        const wasFocusedInItem =
          !itemIdFromElement &&
          activeElement === document.body &&
          lastActiveItemIdRef.current;

        if (isWithinItem || wasFocusedInItem) {
          // Input within item is focused (or was just unfocused) → focus the item
          e.preventDefault();
          setFocusedItemId(itemId);
          // Focus the container so subsequent keyboard events are captured
          containerRef.current?.focus();
        } else if (focusedItemId) {
          // Item has focus → clear focus entirely
          e.preventDefault();
          setFocusedItemId(null);
          lastActiveItemIdRef.current = null;
          containerRef.current?.focus();
        }
        return;
      }

      // Action shortcuts (only when an item is focused with focus ring)
      // Single key press triggers action — skip if focus is inside an input field
      if (
        focusedItemId &&
        isContainerFocused &&
        !e.ctrlKey &&
        !e.metaKey &&
        !e.altKey
      ) {
        const key = e.key.toLowerCase();
        let action: TimelineItemAction | null = null;

        switch (key) {
          case "f": // Star (Favorite)
          case "s": // Star (alternative)
            action = "star";
            break;
          case "l": // Link
            action = "link";
            break;
          case "r": // Reset
            action = "reset";
            break;
          case "d": // Delete
            action = "delete";
            break;
          case "e": // Focus content editor
            if (containerRef.current) {
              e.preventDefault();
              setFocusedItemId(null); // Clear focus ring since user is entering content
              focusContentEditorInItem(
                listRef.current?.element ?? containerRef.current,
                focusedItemId,
              );
            }
            return;
        }

        if (action) {
          e.preventDefault();
          onItemActionClick?.(focusedItemId, action);
        }
      }
    },
    [
      focusedItemIndex,
      focusedItemId,
      displayItems,
      setFocusedItemId,
      onItemActionClick,
      onAddEntry,
      listRef,
      containerRef,
    ],
  );

  // Combined keyboard handler that delegates based on mode
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (mode === "display") {
        handleDisplayModeKeyDown(e);
      } else {
        handleEditModeKeyDown(e);
      }
    },
    [mode, handleDisplayModeKeyDown, handleEditModeKeyDown],
  );

  return {
    handleKeyDown,
    handleFocusIn,
  };
};
